import { Directive, ElementRef, HostListener, inject } from '@angular/core';

/**
 * Strips HTML formatting from pasted content, keeping only plain text.
 *
 * Normalizes whitespace, removes zero-width characters, decodes HTML entities,
 * and collapses excessive blank lines.
 *
 * @example
 * ```html
 * <textarea appPasteStrip formControlName="description"></textarea>
 * ```
 */
@Directive({
  selector: 'textarea[appPasteStrip]',
})
export class PasteStripDirective {
  private readonly elementRef = inject<ElementRef<HTMLTextAreaElement>>(ElementRef);

  @HostListener('paste', ['$event'])
  public onPaste(event: ClipboardEvent): void {
    const raw =
      event.clipboardData?.getData('text/plain')?.trim() ||
      event.clipboardData?.getData('text/html')?.trim() ||
      '';

    if (!raw) return;

    const text = this.toPlainText(raw);

    event.preventDefault();

    const textarea = this.elementRef.nativeElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = textarea.value.slice(0, start);
    const after = textarea.value.slice(end);

    textarea.value = before + text + after;
    textarea.selectionStart = textarea.selectionEnd = start + text.length;

    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private toPlainText(input: string): string {
    if (!input) return '';

    const doc = new DOMParser().parseFromString(input, 'text/html');
    let out = (doc.body as HTMLElement).innerText ?? doc.body.textContent ?? input;

    out = out
      .replace(/\u00A0/g, ' ')
      .replace(/\u200B|\u200C|\u200D|\uFEFF/g, '')
      .replace(/\r/g, '')
      .replace(/[ \t\f\v]+/g, ' ')
      .replace(/ *\n+ */g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .split('\n')
      .map((s) => s.trimEnd())
      .join('\n')
      .trim();

    return out;
  }
}
